import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import {
  fetchPhysiotherapyDetails,
  updatePhysiotherapyStatus,
  addPhysiotherapyNote,
  uploadPhysiotherapyDocuments,
  PHYSIO_STATUSES,
} from "../../../services/physiotherapyApi";
import { imageUrl } from "../../../Basurl/Baseurl";
import ReferralModal from "./ReferralModal";
import SessionModal from "./SessionModal";

export default function CaseDetailModal({
  open,
  onClose,
  caseId,
  onStatusUpdated,
  onOpenLogSession,
}) {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sessions"); // sessions | notes | documents | status

  // New Note state
  const [newNote, setNewNote] = useState({ noteText: "", noteType: "Progress" });
  const [addingNote, setAddingNote] = useState(false);

  // Upload Doc state
  const [newDocName, setNewDocName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Edit Case and Session Modals
  const [editCaseModalOpen, setEditCaseModalOpen] = useState(false);
  const [localSessionModalOpen, setLocalSessionModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);

  // Status Change state
  const [newStatus, setNewStatus] = useState("");
  const [statusNotes, setStatusNotes] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const contentRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }
  }, [open]);

  useEffect(() => {
    if (open && caseId) {
      loadDetails();
    }
  }, [open, caseId]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const data = await fetchPhysiotherapyDetails(caseId);
      const detail = data?.data || data;
      setCaseData(detail);
      setNewStatus(detail?.status || "");
    } catch (err) {
      console.error("Error loading case detail:", err);
      Swal.fire("Error", "Could not load physiotherapy case details", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNoteSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.noteText.trim()) return;

    setAddingNote(true);
    try {
      await addPhysiotherapyNote(caseId, {
        noteText: newNote.noteText.trim(),
        noteType: newNote.noteType,
        author: "Dr. Admin / Physiotherapist",
      });
      setNewNote({ noteText: "", noteType: "Progress" });
      await loadDetails();
      Swal.fire({
        title: "Note Added",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Error", typeof err === "string" ? err : "Failed to add clinical note", "error");
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusChangeSubmit = async (e) => {
    e.preventDefault();
    if (!newStatus) return;

    setUpdatingStatus(true);
    try {
      await updatePhysiotherapyStatus(caseId, newStatus, statusNotes.trim());
      Swal.fire("Status Updated", `Case status changed to ${newStatus}`, "success");
      await loadDetails();
      if (onStatusUpdated) onStatusUpdated();
    } catch (err) {
      Swal.fire("Error", typeof err === "string" ? err : "Failed to update status", "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDocUploadSubmit = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      return Swal.fire("File Required", "Please choose a file to upload", "warning");
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("documentName", newDocName.trim() || "Physiotherapy Document");
      selectedFiles.forEach((file) => {
        formData.append("documents", file);
      });

      await uploadPhysiotherapyDocuments(caseId, formData);
      Swal.fire("Success", "Documents uploaded successfully", "success");
      setNewDocName("");
      setSelectedFiles([]);
      await loadDetails();
    } catch (err) {
      Swal.fire("Error", typeof err === "string" ? err : "Failed to upload document", "error");
    } finally {
      setUploadingDoc(false);
    }
  };

  const getFileUrl = (docOrPath) => {
    if (!docOrPath) return "";

    const rawPath =
      typeof docOrPath === "string"
        ? docOrPath
        : (docOrPath?.file ||
           docOrPath?.filePath ||
           docOrPath?.filepath ||
           docOrPath?.fileUrl ||
           docOrPath?.file_url ||
           docOrPath?.path ||
           docOrPath?.url ||
           docOrPath?.document ||
           docOrPath?.documentUrl ||
           docOrPath?.fileName ||
           docOrPath?.filename ||
           docOrPath?.attachment ||
           docOrPath?.location ||
           (typeof docOrPath?.name === "string" && /\.(pdf|jpg|jpeg|png|webp|gif|docx?|xlsx?)$/i.test(docOrPath.name) ? docOrPath.name : "") ||
           "");

    if (!rawPath || typeof rawPath !== "string") return "";
    const trimmed = rawPath.trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined" || trimmed === "#") return "";

    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("blob:") ||
      trimmed.startsWith("data:")
    ) {
      return trimmed;
    }

    const cleanPath = trimmed.replace(/\\/g, "/").replace(/^\/+/, "");
    const base = (imageUrl || "").endsWith("/") ? imageUrl : `${imageUrl || ""}/`;
    return `${base}${cleanPath}`;
  };

  const handleOpenDoc = (e, doc) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const url = getFileUrl(doc);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      Swal.fire({
        icon: "warning",
        title: "File Not Available",
        text: "The document file URL could not be found or is invalid.",
      });
    }
  };

  const completed = caseData?.completedSessions || 0;
  const prescribed = caseData?.prescribedSessions || 1;
  const progressPercent = Math.min(100, Math.round((completed / prescribed) * 100));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="physio-detail-dialog"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      {/* Sticky Branded Navy Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "#ffffff",
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              color: "#38bdf8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
            }}
          >
            <i className="fa-solid fa-heart-pulse" />
          </div>
          <div>
            <h5 className="mb-0 text-white fw-bold" style={{ fontSize: "17px", letterSpacing: "-0.01em" }}>
              {caseData?.patientName || "Patient"} - Physiotherapy Case Record
            </h5>
            <small style={{ color: "rgba(255,255,255,0.75)", fontSize: "12.5px" }}>
              Patient ID: <strong>{caseData?.patientId}</strong> | Category:{" "}
              <strong>{caseData?.category}</strong> | Priority:{" "}
              <span className="badge bg-secondary text-white ms-1">{caseData?.priority}</span>
            </small>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-1 shadow-sm fw-bold"
            style={{ borderRadius: "8px", fontSize: "13px", padding: "6px 14px", backgroundColor: "#0284c7", borderColor: "#0284c7" }}
            onClick={() => setEditCaseModalOpen(true)}
          >
            <i className="fa fa-pencil" /> Edit Case
          </button>
          <button
            type="button"
            className="btn btn-success d-flex align-items-center gap-1 shadow-sm fw-bold"
            style={{ borderRadius: "8px", fontSize: "13px", padding: "6px 14px" }}
            onClick={() => {
              if (onOpenLogSession && caseData) {
                onOpenLogSession(caseData);
              } else {
                setEditingSession(null);
                setLocalSessionModalOpen(true);
              }
            }}
          >
            <i className="fa fa-plus-circle" /> Log Session
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: "34px",
              height: "34px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            title="Close"
          >
            <i className="fa-solid fa-xmark fs-6" />
          </button>
        </div>
      </div>

      {/* Scrollable Modal Body */}
      <DialogContent
        ref={contentRef}
        style={{
          overflowY: "auto",
          padding: "20px 24px",
          backgroundColor: "#f8fafc",
        }}
      >
        {loading && !caseData ? (
          <div className="text-center py-5">
            <i className="fa fa-spinner fa-spin fa-2x text-primary mb-2" />
            <p className="text-muted">Loading case details...</p>
          </div>
        ) : !caseData ? (
          <div className="text-center py-5 text-muted">Case not found</div>
        ) : (
          <div>
            {/* Top Case Summary Card */}
            <div
              className="card mb-3 border-0 shadow-sm"
              style={{ borderRadius: "12px", background: "#ffffff" }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-center">
                  <div className="col-md-7">
                    <h6 className="fw-bold text-primary mb-1" style={{ fontSize: "15px" }}>
                      {caseData.clinicalDiagnosis}
                    </h6>
                    <p className="text-muted small mb-2">
                      {caseData.referralReason || "No specific referral complaint provided."}
                    </p>
                    <div className="d-flex flex-wrap gap-3 small text-muted">
                      <span>
                        <i className="fa fa-map-marker text-danger me-1" />
                        Area: <strong>{caseData.affectedBodyPart || "General"}</strong>
                      </span>
                      <span>
                        <i className="fa fa-user-md text-info me-1" />
                        Therapist: <strong>{caseData.assignedPhysiotherapist?.name || "Unassigned"}</strong>
                      </span>
                      <span>
                        <i className="fa fa-repeat text-success me-1" />
                        Frequency: <strong>{caseData.sessionFrequency || "As needed"}</strong>
                      </span>
                    </div>
                  </div>

                  <div className="col-md-5 border-start ps-4">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-semibold text-dark">
                        Rehabilitation Progress:
                      </span>
                      <span className="badge bg-primary fw-bold">
                        {completed} / {prescribed} Sessions ({progressPercent}%)
                      </span>
                    </div>
                    <div className="progress mb-2" style={{ height: "8px" }}>
                      <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                    <div className="d-flex justify-content-between small text-muted">
                      <span>Status: <strong className="text-dark">{caseData.status}</strong></span>
                      <span>Baseline Pain: <strong>{caseData.initialAssessment?.baselinePainScore ?? "N/A"}/10</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <ul className="nav nav-pills mb-3 gap-2">
              <li className="nav-item">
                <button
                  className={`btn btn-sm ${activeTab === "sessions" ? "btn-primary shadow-sm" : "btn-light border"}`}
                  style={{ borderRadius: "8px", fontWeight: "600", padding: "6px 14px" }}
                  onClick={() => setActiveTab("sessions")}
                >
                  <i className="fa fa-list-check me-1" /> Sessions ({caseData.sessions?.length || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`btn btn-sm ${activeTab === "notes" ? "btn-primary shadow-sm" : "btn-light border"}`}
                  style={{ borderRadius: "8px", fontWeight: "600", padding: "6px 14px" }}
                  onClick={() => setActiveTab("notes")}
                >
                  <i className="fa fa-notes-medical me-1" /> Clinical Notes ({caseData.clinicalNotes?.length || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`btn btn-sm ${activeTab === "documents" ? "btn-primary shadow-sm" : "btn-light border"}`}
                  style={{ borderRadius: "8px", fontWeight: "600", padding: "6px 14px" }}
                  onClick={() => setActiveTab("documents")}
                >
                  <i className="fa fa-folder-open me-1" /> Documents ({caseData.documents?.length || 0})
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`btn btn-sm ${activeTab === "status" ? "btn-primary shadow-sm" : "btn-light border"}`}
                  style={{ borderRadius: "8px", fontWeight: "600", padding: "6px 14px" }}
                  onClick={() => setActiveTab("status")}
                >
                  <i className="fa fa-sliders me-1" /> Case Status & Objectives
                </button>
              </li>
            </ul>

            {/* Tab 1: Sessions List */}
            {activeTab === "sessions" && (
              <div>
                {!caseData.sessions || caseData.sessions.length === 0 ? (
                  <div className="card border-0 shadow-sm text-center py-5" style={{ borderRadius: "12px" }}>
                    <i className="fa fa-calendar-times-o fa-3x text-muted mb-2" />
                    <h6 className="text-dark fw-bold">No sessions logged yet</h6>
                    <p className="text-muted small">
                      Start tracking physiotherapy by recording the patient's first session.
                    </p>
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm btn-success px-3"
                        onClick={() => onOpenLogSession && onOpenLogSession(caseData)}
                      >
                        <i className="fa fa-plus me-1" /> Record Session #1
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {caseData.sessions.map((sess, idx) => (
                      <div
                        key={sess._id || idx}
                        className="card border-0 shadow-sm"
                        style={{ borderRadius: "12px" }}
                      >
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-primary px-2 py-1">
                                Session #{sess.sessionNumber || idx + 1}
                              </span>
                              <span className="fw-bold text-dark">
                                {sess.sessionDate ? new Date(sess.sessionDate).toLocaleDateString() : "No Date"}
                              </span>
                              <span className="text-muted small">
                                ({sess.durationMinutes || 45} mins)
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-light text-dark border">
                                Pain: {sess.painLevelBefore ?? "-"} → {sess.painLevelAfter ?? "-"} / 10
                              </span>
                              <span
                                className={`badge ${
                                  sess.status === "Completed"
                                    ? "bg-success"
                                    : sess.status === "Missed"
                                    ? "bg-danger"
                                    : "bg-warning text-dark"
                                }`}
                              >
                                {sess.status}
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm py-0 px-2 d-flex align-items-center gap-1 ms-1"
                                style={{ fontSize: "11.5px", borderRadius: "6px" }}
                                onClick={() => {
                                  setEditingSession(sess);
                                  setLocalSessionModalOpen(true);
                                }}
                                title="Edit Session Details"
                              >
                                <i className="fa fa-pencil" /> Edit
                              </button>
                            </div>
                          </div>

                          {sess.modalitiesUsed && sess.modalitiesUsed.length > 0 && (
                            <div className="mb-2 d-flex flex-wrap gap-1">
                              {sess.modalitiesUsed.map((m, mIdx) => (
                                <span
                                  key={mIdx}
                                  className="badge bg-light text-primary border"
                                  style={{ fontSize: "11px" }}
                                >
                                  {m}
                                </span>
                              ))}
                            </div>
                          )}

                          {((sess.exercises && sess.exercises.length > 0) || (sess.exercisesPerformed && sess.exercisesPerformed.length > 0)) && (
                            <div className="small text-muted mb-2">
                              <strong>Exercises:</strong>{" "}
                              {(sess.exercises && sess.exercises.length > 0 ? sess.exercises : sess.exercisesPerformed || [])
                                .map(
                                  (e) =>
                                    `${e.exerciseName} (${e.sets || 3}x${e.repetitions || 10}${
                                      e.holdTimeSeconds ? ` hold ${e.holdTimeSeconds}s` : ""
                                    })`
                                )
                                .join(", ")}
                            </div>
                          )}

                          {sess.progressRemarks && (
                            <div className="p-2 rounded bg-light small text-dark">
                              <strong>Remarks:</strong> {sess.progressRemarks}
                            </div>
                          )}

                          {sess.attachments && sess.attachments.length > 0 && (
                            <div className="mt-2 d-flex flex-wrap gap-2">
                              {sess.attachments.map((att, aIdx) => (
                                <a
                                  key={aIdx}
                                  href={getFileUrl(att) || "#"}
                                  onClick={(e) => handleOpenDoc(e, att)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                  style={{ fontSize: "11px" }}
                                >
                                  <i className="fa fa-file" /> {att.documentName || att.fileName || att.name || `Attachment ${aIdx + 1}`}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Clinical Notes */}
            {activeTab === "notes" && (
              <div>
                <form onSubmit={handleAddNoteSubmit} className="card border-0 shadow-sm p-3 mb-3" style={{ borderRadius: "12px" }}>
                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "14px" }}>Add Clinical Note</h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <select
                        className="form-select form-select-sm"
                        value={newNote.noteType}
                        onChange={(e) => setNewNote({ ...newNote, noteType: e.target.value })}
                      >
                        <option value="Initial Evaluation">Initial Evaluation</option>
                        <option value="Progress">Progress Note</option>
                        <option value="Reassessment">Reassessment</option>
                        <option value="Discharge Summary">Discharge Summary</option>
                      </select>
                    </div>
                    <div className="col-md-7">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Type clinical observation, doctor's directive, or therapist note..."
                        value={newNote.noteText}
                        onChange={(e) => setNewNote({ ...newNote, noteText: e.target.value })}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="submit"
                        className="btn btn-sm btn-primary w-100"
                        disabled={addingNote}
                      >
                        {addingNote ? <i className="fa fa-spinner fa-spin" /> : "Post Note"}
                      </button>
                    </div>
                  </div>
                </form>

                {!caseData.clinicalNotes || caseData.clinicalNotes.length === 0 ? (
                  <div className="card border-0 shadow-sm text-center py-4 text-muted" style={{ borderRadius: "12px" }}>
                    No clinical notes recorded yet.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {caseData.clinicalNotes.map((note, idx) => (
                      <div key={note._id || idx} className="card border-0 shadow-sm p-3" style={{ borderRadius: "12px" }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="badge bg-info text-white">{note.noteType || "Progress"}</span>
                          <small className="text-muted">
                            {(note.date || note.createdAt) ? new Date(note.date || note.createdAt).toLocaleString() : ""}
                          </small>
                        </div>
                        <p className="mb-1 text-dark" style={{ fontSize: "13.5px" }}>{note.note || note.noteText || ""}</p>
                        <small className="text-muted">
                          Author:{" "}
                          {typeof note.author === "object"
                            ? note.author?.name || note.author?.email || "Clinical Staff"
                            : note.author ||
                              (typeof note.addedBy === "object"
                                ? note.addedBy?.name || note.addedBy?.email
                                : note.addedBy) ||
                              "Clinical Staff"}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Documents */}
            {activeTab === "documents" && (
              <div>
                <form onSubmit={handleDocUploadSubmit} className="card border-0 shadow-sm p-3 mb-3" style={{ borderRadius: "12px" }}>
                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "14px" }}>Upload Case Document</h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Document Name (e.g. Discharge Summary, MRI Report)"
                        value={newDocName}
                        onChange={(e) => setNewDocName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="file"
                        multiple
                        className="form-control form-control-sm"
                        onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="submit"
                        className="btn btn-sm btn-primary w-100"
                        disabled={uploadingDoc}
                      >
                        {uploadingDoc ? <i className="fa fa-spinner fa-spin" /> : "Upload"}
                      </button>
                    </div>
                  </div>
                </form>

                {!caseData.documents || caseData.documents.length === 0 ? (
                  <div className="card border-0 shadow-sm text-center py-4 text-muted" style={{ borderRadius: "12px" }}>
                    No documents uploaded for this case.
                  </div>
                ) : (
                  <div className="row g-3">
                    {caseData.documents.map((doc, idx) => (
                      <div key={doc._id || idx} className="col-md-4">
                        <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: "12px" }}>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="fa fa-file-text-o fa-2x text-primary" />
                            <div>
                              <strong className="text-dark d-block text-truncate" style={{ maxWidth: "200px", fontSize: "13px" }}>
                                {doc.documentName || doc.fileName || (typeof doc.file === "string" ? doc.file.split("/").pop()?.replace(/^\d+-/, "") : "") || `Doc ${idx + 1}`}
                              </strong>
                              <small className="text-muted">
                                {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ""}
                              </small>
                            </div>
                          </div>
                          <a
                            href={getFileUrl(doc) || "#"}
                            onClick={(e) => handleOpenDoc(e, doc)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary w-100 mt-auto"
                            style={{ borderRadius: "6px" }}
                          >
                            <i className="fa fa-download me-1" /> View / Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: Status & Objectives */}
            {activeTab === "status" && (
              <div>
                <form onSubmit={handleStatusChangeSubmit} className="card border-0 shadow-sm p-3 mb-3" style={{ borderRadius: "12px" }}>
                  <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>Update Case Workflow Status</h6>
                  <div className="row g-3 align-items-end">
                    <div className="col-md-4">
                      <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>Status</label>
                      <select
                        className="form-select"
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        style={{ fontSize: "13.5px" }}
                      >
                        {PHYSIO_STATUSES.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>Status Notes / Reason</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Patient discharged, goals achieved, therapy paused"
                        value={statusNotes}
                        onChange={(e) => setStatusNotes(e.target.value)}
                        style={{ fontSize: "13.5px" }}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={updatingStatus}
                        style={{ borderRadius: "8px", fontSize: "13.5px" }}
                      >
                        {updatingStatus ? <i className="fa fa-spinner fa-spin" /> : "Save Status"}
                      </button>
                    </div>
                  </div>
                </form>

                <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
                  <div className="card-header bg-white fw-bold text-dark border-bottom" style={{ fontSize: "14px" }}>
                    Plan & Clinical Objectives
                  </div>
                  <div className="card-body p-3" style={{ fontSize: "13.5px" }}>
                    <p className="mb-2">
                      <strong>Treatment Goals:</strong> {caseData.treatmentGoals || "Not specified"}
                    </p>
                    <p className="mb-2">
                      <strong>Special Precautions:</strong> {caseData.specialInstructions || "None"}
                    </p>
                    <p className="mb-2">
                      <strong>Initial ROM / Mobility Notes:</strong>{" "}
                      {caseData.initialAssessment?.rangeOfMotionNotes || "No initial notes"}
                    </p>
                    <div className="mb-0">
                      <strong>Therapy / Modalities:</strong>
                      {Array.isArray(caseData.modalitiesUsed) && caseData.modalitiesUsed.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {caseData.modalitiesUsed.map((m, mIdx) => (
                            <span
                              key={mIdx}
                              className="badge bg-light text-primary border"
                              style={{ fontSize: "11px" }}
                            >
                              {typeof m === "string" ? m : m?.name || String(m)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted"> None selected</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {/* Sticky Action Footer */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "14px 24px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "12px",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          className="btn btn-light px-4 border fw-semibold"
          style={{ borderRadius: "8px", fontSize: "13.5px" }}
          onClick={onClose}
        >
          Close
        </button>
      </div>

      {/* Edit Case Modal */}
      <ReferralModal
        open={editCaseModalOpen}
        onClose={() => setEditCaseModalOpen(false)}
        editCase={caseData}
        onSuccess={() => {
          loadDetails();
          if (onStatusUpdated) onStatusUpdated();
        }}
      />

      {/* Log / Edit Session Modal */}
      <SessionModal
        open={localSessionModalOpen}
        onClose={() => {
          setLocalSessionModalOpen(false);
          setEditingSession(null);
        }}
        physioCase={caseData}
        editingSession={editingSession}
        onSuccess={() => {
          loadDetails();
          if (onStatusUpdated) onStatusUpdated();
        }}
      />
    </Dialog>
  );
}
